<template>
  <li class="header-notification-nav" ref="opinionNav">
    <button
      class="header-action-btn"
      type="button"
      @click.stop="openModal"
      title="Opinion de Cumplimiento"
    >
      <i class="fa fa-check-square-o"></i>
    </button>
  </li>

  <teleport to="body">
    <div v-if="showModal" class="oc-modal-backdrop" @click="closeModal">
      <div class="oc-modal" role="dialog" aria-modal="true" @click.stop>
        <div v-if="processing" class="oc-processing-overlay">
          <div class="glo-card oc-processing-card">
            <div class="spinner-border" role="status" aria-hidden="true"></div>
            <div class="glo-text oc-processing-text"><img src="/minilogo.png" alt="TusCFDIs" class="oc-processing-logo" /></div>
          </div>
        </div>

        <div class="oc-modal-header">
          <div>
            <h5 class="oc-modal-title">Opinión de Cumplimiento</h5>
            <small class="oc-meta" v-if="latest && latest.generated_at">
              Última generación: {{ formatDate(latest.generated_at) }}
            </small>
            <small class="oc-meta" v-else>
              Aún no se ha generado la opinión de cumplimiento para este RFC.
            </small>
          </div>
          <button class="oc-close-btn" type="button" @click="closeModal" aria-label="Cerrar">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <div class="oc-modal-body">
          <div v-if="loading" class="oc-state-msg">Cargando opinión...</div>

          <div v-else-if="!currentRfc" class="oc-state-msg">
            Selecciona un RFC para consultar o generar la opinión.
          </div>

          <div v-else-if="!latest" class="oc-empty-wrap">
            <img src="/onboarding/opinion.png" alt="Opinión de Cumplimiento" class="oc-empty-image" />
          </div>

          <div v-else-if="previewUrl" class="oc-preview-wrap">
            <iframe
              :src="previewUrl"
              class="oc-preview-frame"
              title="Vista previa de opinión"
            ></iframe>
          </div>

          <div v-else class="oc-state-msg">
            No hay PDF disponible para previsualizar. Puedes generar una nueva opinión.
          </div>

          <small v-if="error" class="oc-error">{{ error }}</small>
        </div>

        <div class="oc-modal-footer">
          <button
            type="button"
            class="oc-option-btn"
            :disabled="processing || !currentRfc"
            @click="generateOpinion"
          >
            {{ processing ? 'Generando...' : 'Generar Opinión' }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import opinionComplianceService from '@/services/opinionCompliance';

export default {
  name: 'ComplianceOpinionHeaderAction',
  data() {
    return {
      showModal: false,
      loading: false,
      processing: false,
      latest: null,
      error: '',
      previewUrl: '',
    };
  },
  computed: {
    currentRfc() {
      return String(localStorage.getItem('rfc') || '').trim().toUpperCase();
    },
  },
  methods: {
    extractPayload(response) {
      const isAxiosRaw = !!(response && typeof response === 'object' && 'status' in response && 'config' in response);
      return isAxiosRaw ? response.data : response;
    },
    emitHeaderDropdownOpen(source) {
      window.dispatchEvent(new CustomEvent('header-dropdown-open', { detail: { source } }));
    },
    handleHeaderDropdownOpen(event) {
      const source = event && event.detail ? event.detail.source : '';
      if (source !== 'opinion') {
        this.showModal = false;
      }
    },
    async openModal() {
      if (this.showModal) {
        return;
      }

      this.showModal = true;
      this.emitHeaderDropdownOpen('opinion');
      await this.loadLatest();
    },
    closeModal() {
      if (this.processing) {
        return;
      }

      this.showModal = false;
    },
    async loadLatest() {
      this.loading = true;
      this.error = '';
      this.latest = null;
      this.previewUrl = '';

      if (!this.currentRfc) {
        this.loading = false;
        this.error = 'Selecciona un RFC para consultar la opinión.';
        return;
      }

      try {
        const response = await opinionComplianceService.getLatest(this.currentRfc);
        const payload = this.extractPayload(response);
        this.latest = payload?.data?.opinion || null;

        if (this.latest?.id) {
          await this.loadPreview(this.latest.id);
        }
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo consultar la opinión actual.';
      }

      this.loading = false;
    },
    async generateOpinion() {
      if (!this.currentRfc || this.processing) {
        return;
      }

      this.processing = true;
      this.error = '';

      try {
        const response = await opinionComplianceService.generate(this.currentRfc);
        const payload = this.extractPayload(response);
        this.latest = payload?.data?.opinion || null;

        if (this.latest?.id) {
          await this.loadPreview(this.latest.id);
        }
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo generar la opinión.';
      }

      this.processing = false;
    },
    async loadPreview(opinionId = null) {
      if (!this.currentRfc) {
        return;
      }

      try {
        const response = await opinionComplianceService.getPreview(this.currentRfc, opinionId);
        const payload = this.extractPayload(response);
        this.previewUrl = payload?.data?.preview_data_url || '';
      } catch (error) {
        this.previewUrl = '';
        this.error = error?.response?.data?.message || 'No se pudo cargar la previsualización de la opinión.';
      }
    },
    async downloadLatest() {
      if (!this.currentRfc || !this.latest || this.processing) {
        return;
      }

      this.processing = true;
      this.error = '';

      try {
        const response = await opinionComplianceService.getDownloadLink(this.currentRfc, this.latest.id);
        const payload = this.extractPayload(response);
        const url = payload?.data?.download_url || '';

        if (!url) {
          this.error = 'No se encontró URL de descarga para la opinión.';
        } else {
          window.open(url, '_blank');
        }
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo descargar la opinión.';
      }

      this.processing = false;
    },
    formatDate(dateValue) {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) {
        return String(dateValue || '');
      }

      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    handleRfcsStorageUpdated() {
      if (this.showModal) {
        this.loadLatest();
      }
    },
    handleStorageEvent(event) {
      if (!event || event.key === null || event.key === 'rfc' || event.key === 'rfcs') {
        if (this.showModal) {
          this.loadLatest();
        }
      }
    },
    handleEscapeClose(event) {
      if (!event) {
        return;
      }

      if (event.key === 'Escape' && this.showModal) {
        this.closeModal();
      }
    },
  },
  mounted() {
    window.addEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.addEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.addEventListener('storage', this.handleStorageEvent);
    window.addEventListener('keydown', this.handleEscapeClose);
  },
  beforeUnmount() {
    window.removeEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.removeEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.removeEventListener('storage', this.handleStorageEvent);
    window.removeEventListener('keydown', this.handleEscapeClose);
  },
  beforeDestroy() {
    window.removeEventListener('header-dropdown-open', this.handleHeaderDropdownOpen);
    window.removeEventListener('rfcs-storage-updated', this.handleRfcsStorageUpdated);
    window.removeEventListener('storage', this.handleStorageEvent);
    window.removeEventListener('keydown', this.handleEscapeClose);
  },
};
</script>

<style scoped>
.header-notification-nav {
  position: relative;
  display: flex;
  align-items: center;
}

.header-action-btn {
  border: 0;
  background: var(--white);
  height: 52px;
  min-height: 52px;
  min-width: 46px;
  padding: 0 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid var(--sidebar-border);
  transition: all 0.15s ease;
  position: relative;
}

.header-action-btn i {
  font-size: 16px;
  color: var(--theme-font-color);
  opacity: 0.8;
}

.header-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px var(--sidebar-border);
}

.header-action-btn:hover i {
  color: var(--theme-deafult);
  opacity: 1;
}

.oc-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(11, 22, 44, 0.48);
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.oc-modal {
  position: relative;
  width: min(940px, 100%);
  height: min(86vh, 760px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 24px 60px rgba(17, 30, 58, 0.35);
  border: 1px solid var(--sidebar-border);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.oc-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--sidebar-border);
  background: #f8fafc;
}

.oc-modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--theme-font-color);
}

.oc-close-btn {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid var(--sidebar-border);
  background: #fff;
  color: var(--theme-font-color);
}

.oc-modal-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.oc-preview-wrap {
  flex: 1;
  min-height: 0;
  border: 1px solid var(--sidebar-border);
  border-radius: 10px;
  overflow: hidden;
  background: #fafcff;
}

.oc-preview-frame {
  width: 100%;
  height: 100%;
  border: 0;
}

.oc-state-msg {
  border: 1px dashed var(--sidebar-border);
  border-radius: 10px;
  background: #f8fafc;
  color: var(--theme-body-font-color);
  padding: 20px 16px;
}

.oc-empty-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.oc-empty-image {
  width: auto;
  height: auto;
  max-width: 80%;
  max-height: 100%;
  object-fit: contain;
}

.oc-modal-footer {
  flex-shrink: 0;
  border-top: 1px solid var(--sidebar-border);
  padding: 12px 16px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.oc-meta {
  color: var(--theme-body-font-color);
}

.oc-option-btn {
  border: 1px solid var(--theme-deafult);
  background: var(--theme-deafult);
  color: #fff;
  border-radius: 8px;
  height: 38px;
  font-size: 13px;
  font-weight: 600;
}

.oc-option-btn.secondary {
  background: #fff;
  color: var(--theme-deafult);
}

.oc-option-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.oc-error {
  color: #dc3545;
}

.oc-processing-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oc-processing-card {
  background: #fff;
  border-radius: 10px;
  padding: 16px 18px;
  min-width: 220px;
  max-width: 80vw;
  box-shadow: 0 15px 40px rgba(15, 23, 42, 0.25);
  display: flex;
  align-items: center;
  gap: 12px;
}

.glo-text {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.oc-processing-text {
  display: flex;
  align-items: center;
}

.oc-processing-logo {
  width: 150px;
  max-width: 55vw;
}

.oc-processing-card :deep(.spinner-border) {
  width: 2rem;
  height: 2rem;
}

.oc-processing-card :deep(.spinner-border),
.oc-processing-card :deep(.spinner-border-sm) {
  color: var(--theme-deafult);
}

.oc-processing-card {
  padding: 12px 14px;
  padding: 16px 18px;
}

@media (max-width: 576px) {
  .oc-modal {
    height: min(92vh, 760px);
  }

  .oc-modal-footer {
    flex-direction: column;
  }

  .oc-option-btn {
    width: 100%;
  }
}
</style>
